'use client';

import { useRouter } from 'next/navigation';
import Image from 'next/image';

export default function CookiePage() {
  const router = useRouter();

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff' }}>
      <main style={{ minHeight: '100vh', padding: '3rem 1.5rem' }}>
        <div className="container">
          <div className="card shadow-card">
            {/* Header with Logo and Back Button */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(15, 23, 42, 0.08)' }}>
              <button
                type="button"
                onClick={() => router.push('/')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'transparent',
                  border: 'none',
                  color: '#1d4ed8',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                <span>←</span>
                <span>Back</span>
              </button>

              <button
                type="button"
                onClick={() => router.push('/')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
                aria-label="Go to home"
              >
                <Image src="/logo.png" alt="Amaya Stories logo" width={40} height={40} priority />
              </button>
            </div>

            {/* Title */}
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <h1 style={{ fontSize: '2rem', marginBottom: '1rem', lineHeight: 1.3 }}>
                Cookie Policy
              </h1>
              <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                Last updated: October 22, 2025
              </p>
            </div>

            <div style={{ color: '#475569', lineHeight: 1.7, maxWidth: '100%' }}>
              {/* Introduction */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  Introduction
                </h2>
                <p style={{ marginBottom: '1rem' }}>
                  This cookie policy explains how Amaya Stories uses cookies and similar technologies on our platform.
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  This policy explains what cookies are, how we use them, and what choices you have regarding their use. By using our platform, you agree to the use of essential technical cookies necessary for the platform&apos;s functionality.
                </p>
              </section>

              {/* What Are Cookies */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  What Are Cookies?
                </h2>
                <p style={{ marginBottom: '1rem' }}>
                  A cookie is a small file consisting of letters and numbers that, with your consent, we save to your device&apos;s browser and your device&apos;s hard drive. Cookies help us distinguish you from other users of our website, thus ensuring a more pleasant user experience and allowing us to improve our platform.
                </p>
              </section>

              {/* Types of Cookies */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  Types of Cookies We Use
                </h2>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.75rem' }}>
                    Essential Technical Cookies
                  </h3>
                  <p style={{ marginBottom: '0.5rem' }}>
                    These cookies are necessary for the platform to function properly. They enable core functionality such as:
                  </p>
                  <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                    <li style={{ marginBottom: '0.5rem' }}>Maintaining login sessions (NextAuth session cookies)</li>
                    <li style={{ marginBottom: '0.5rem' }}>Managing book generation workflow</li>
                    <li style={{ marginBottom: '0.5rem' }}>Security and authentication</li>
                    <li style={{ marginBottom: '0.5rem' }}>Session recognition and user preferences</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.75rem' }}>
                    Functional / Performance Cookies
                  </h3>
                  <p style={{ marginBottom: '0.5rem' }}>
                    These cookies improve user experience by:
                  </p>
                  <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                    <li style={{ marginBottom: '0.5rem' }}>Remembering your child&apos;s profile settings</li>
                    <li style={{ marginBottom: '0.5rem' }}>Saving theme preferences and language choices</li>
                    <li style={{ marginBottom: '0.5rem' }}>Remembering your last book generation settings</li>
                    <li style={{ marginBottom: '0.5rem' }}>Enhancing platform performance</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.75rem' }}>
                    Analytical Cookies
                  </h3>
                  <p style={{ marginBottom: '0.5rem' }}>
                    These cookies collect anonymous statistics about:
                  </p>
                  <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                    <li style={{ marginBottom: '0.5rem' }}>Platform usage and performance</li>
                    <li style={{ marginBottom: '0.5rem' }}>Popular book themes and age ranges</li>
                    <li style={{ marginBottom: '0.5rem' }}>User behavior patterns (anonymized)</li>
                    <li style={{ marginBottom: '0.5rem' }}>Feature usage analytics</li>
                  </ul>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.75rem' }}>
                    Payment Cookies (Stripe)
                  </h3>
                  <p>
                    When you purchase credits or subscriptions, Stripe (our payment processor) may set cookies to process payments securely. These cookies are necessary for transaction security and fraud prevention.
                  </p>
                </div>
              </section>

              {/* Data Collection Table */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  Detailed Cookie Information
                </h2>
                <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid rgba(15, 23, 42, 0.08)' }}>
                    <thead style={{ background: '#f8fafc' }}>
                      <tr>
                        <th style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)', textAlign: 'left', fontWeight: 600, color: '#0f172a' }}>Cookie Name</th>
                        <th style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)', textAlign: 'left', fontWeight: 600, color: '#0f172a' }}>Purpose</th>
                        <th style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)', textAlign: 'left', fontWeight: 600, color: '#0f172a' }}>Type</th>
                        <th style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)', textAlign: 'left', fontWeight: 600, color: '#0f172a' }}>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>next-auth.session-token</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Authentication and login session</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Essential</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Session (30 days)</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>kid_profile</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Remember child profile settings</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Functional</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>1 year</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>__stripe_*</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Payment processing and fraud prevention</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Essential</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Session</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>preferences</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Theme, language, and UI preferences</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>Functional</td>
                        <td style={{ padding: '0.75rem', border: '1px solid rgba(15, 23, 42, 0.08)' }}>1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Your Choices */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  Your Choices and Control
                </h2>
                <p style={{ marginBottom: '1rem' }}>
                  You have control over cookies and can manage them in several ways:
                </p>
                <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                  <li style={{ marginBottom: '0.75rem' }}>
                    <strong>Browser Settings:</strong> You can delete or block cookies in your browser at any time through your browser settings.
                  </li>
                  <li style={{ marginBottom: '0.75rem' }}>
                    <strong>Opt-Out:</strong> If you do not agree to the use of non-essential cookies, you can disable them in your browser settings.
                  </li>
                  <li style={{ marginBottom: '0.75rem' }}>
                    <strong>Impact on Functionality:</strong> Please note that certain platform functions may not work properly if essential cookies are disabled, particularly authentication and payment features.
                  </li>
                </ul>

                <div style={{ background: '#fef3c7', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #fbbf24', marginTop: '1rem' }}>
                  <p style={{ margin: 0, color: '#92400e' }}>
                    <strong>Important:</strong> By using Amaya Stories, you agree to the use of essential technical cookies. Disabling these cookies may prevent you from creating books and using core features of the platform.
                  </p>
                </div>
              </section>

              {/* Legal Basis */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  Legal Basis
                </h2>
                <p style={{ marginBottom: '1rem' }}>
                  This cookie policy is part of our Privacy Policy and Terms of Service, which comply with:
                </p>
                <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}>GDPR (EU Regulation 2016/679)</li>
                  <li style={{ marginBottom: '0.5rem' }}>COPPA (Children&apos;s Online Privacy Protection Act)</li>
                  <li style={{ marginBottom: '0.5rem' }}>Other applicable US and international privacy legislation</li>
                </ul>
              </section>

              {/* Your Rights */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  Your Rights
                </h2>
                <p style={{ marginBottom: '1rem' }}>
                  You have the following rights regarding your data and cookies:
                </p>
                <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem', marginBottom: '1rem' }}>
                  <li style={{ marginBottom: '0.75rem' }}>
                    <strong>Access your data:</strong> You have the right to request access to your personal data
                  </li>
                  <li style={{ marginBottom: '0.75rem' }}>
                    <strong>Rectify inaccurate data:</strong> You can request correction of any inaccurate information
                  </li>
                  <li style={{ marginBottom: '0.75rem' }}>
                    <strong>Request deletion:</strong> You can request deletion of your personal data (subject to legal limitations)
                  </li>
                  <li style={{ marginBottom: '0.75rem' }}>
                    <strong>Withdraw consent:</strong> You can withdraw consent for non-essential cookies at any time
                  </li>
                  <li style={{ marginBottom: '0.75rem' }}>
                    <strong>Data portability:</strong> You can request your data in a portable format
                  </li>
                </ul>
                <p style={{ marginBottom: '1rem' }}>
                  To exercise your rights, contact us at:{' '}
                  <a href="mailto:info@amayastories.com" style={{ color: '#1d4ed8', textDecoration: 'underline' }}>
                    info@amayastories.com
                  </a>
                </p>
              </section>

              {/* Contact Information */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  Contact Information
                </h2>
                
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.75rem' }}>
                    Data Controller
                  </h3>
                  <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                    <li style={{ marginBottom: '0.5rem' }}>
                      <strong>Company:</strong> Amaya Stories
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                      <strong>Contact Email:</strong>{' '}
                      <a href="mailto:info@amayastories.com" style={{ color: '#1d4ed8', textDecoration: 'underline' }}>
                        info@amayastories.com
                      </a>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                      <strong>Website:</strong>{' '}
                      <a href="https://www.amayastories.com" style={{ color: '#1d4ed8', textDecoration: 'underline' }}>
                        https://www.amayastories.com
                      </a>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Related Policies */}
              <section style={{ marginBottom: '2.5rem' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a', marginBottom: '1rem' }}>
                  Related Policies
                </h2>
                <p style={{ marginBottom: '1rem' }}>
                  For more information about how we handle your data, please review:
                </p>
                <ul style={{ listStyle: 'disc', paddingLeft: '1.5rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}>
                    <a href="/privacy" style={{ color: '#1d4ed8', textDecoration: 'underline' }}>
                      Privacy Policy
                    </a> - Full details on data collection and processing
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    <a href="/terms" style={{ color: '#1d4ed8', textDecoration: 'underline' }}>
                      Terms of Service
                    </a> - Platform usage terms and conditions
                  </li>
                  <li style={{ marginBottom: '0.5rem' }}>
                    <a href="/ai-usage-policy" style={{ color: '#1d4ed8', textDecoration: 'underline' }}>
                      AI Usage Policy
                    </a> - How we use AI to create content
                  </li>
                </ul>
              </section>

              <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(15, 23, 42, 0.08)' }}>
                <p style={{ fontSize: '0.875rem', color: '#64748b' }}>
                  This document was last updated on October 22, 2025
                </p>
              </div>
            </div>

            {/* Back Button */}
            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => router.push('/')}
              >
                Back to app
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
